/**
 * Stock Movement Service
 * Handles creating and managing stock movements
 */

export interface StockMovementLineItem {
  stockCode: string;
  stockDescription: string;
  location: string;
  quantity: string;
  unitPrice: string;
  lineTotal: number;
  adjustmentType?: "increase" | "decrease";
  writeOffReason?: string;
  adjustmentReason?: string;
}

export interface StockMovementFormData {
  reference: string;
  date: Date;
  type: string;
  notes: string;
  fromLocation?: string;
  toLocation?: string;
  writeOffReason?: string;
  adjustmentReason?: string;
  projectCode?: string;
  customerReference?: string;
  supplierReference?: string;
  invoiceNumber?: string;
}

export interface CreateStockMovementInput {
  formData: StockMovementFormData;
  lineItems: StockMovementLineItem[];
  selectedType: string;
}

// In-memory storage for stock movements (will be replaced with backend API)
let stockMovements: any[] = [];
let nextId = 1;

/**
 * Create a new stock movement
 */
export const createStockMovement = async (input: CreateStockMovementInput): Promise<{ success: boolean; movementId: string }> => {
  const { formData, lineItems, selectedType } = input;

  // Generate movement ID
  const movementId = `MOV${nextId.toString().padStart(3, "0")}`;
  nextId++;

  // Create movement records for each line item
  const movements = lineItems.map((item, index) => {
    let quantity = parseFloat(item.quantity) || 0;
    
    // Adjust quantity based on movement type
    if (selectedType === "Sales" || selectedType === "Write-off") {
      quantity = -Math.abs(quantity); // Negative for outbound movements
    } else if (selectedType === "Manual Adjustment" && item.adjustmentType === "decrease") {
      quantity = -Math.abs(quantity);
    }

    return {
      id: `${movementId}-${index + 1}`,
      reference: formData.reference,
      date: formData.date,
      type: selectedType,
      source: "Manual" as const,
      stockCode: item.stockCode,
      stockDescription: item.stockDescription,
      location: selectedType === "Transfer Between Locations" 
        ? formData.toLocation || null 
        : item.location || null,
      fromLocation: selectedType === "Transfer Between Locations" ? formData.fromLocation : undefined,
      toLocation: selectedType === "Transfer Between Locations" ? formData.toLocation : undefined,
      quantity,
      unitPrice: parseFloat(item.unitPrice) || 0,
      lineTotal: item.lineTotal,
      userName: "Current User", // Will be replaced with actual user from auth
      notes: formData.notes || null,
      writeOffReason: item.writeOffReason || formData.writeOffReason || null,
      adjustmentReason: item.adjustmentReason || formData.adjustmentReason || null,
      projectCode: formData.projectCode || null,
      customerReference: formData.customerReference || null,
      supplierReference: formData.supplierReference || null,
      invoiceNumber: formData.invoiceNumber || null,
      createdAt: new Date().toISOString(),
    };
  });

  // Store movements in memory
  stockMovements.push(...movements);

  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 500));

  return { success: true, movementId };
};

/**
 * Get all stock movements (for display in history)
 */
export const getStockMovements = (): any[] => {
  return [...stockMovements].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
};

/**
 * Get a single stock movement by ID
 */
export const getStockMovementById = (id: string): any | null => {
  return stockMovements.find(m => m.id === id) || null;
};

/**
 * Clear all stock movements (for testing)
 */
export const clearStockMovements = (): void => {
  stockMovements = [];
  nextId = 1;
};

/**
 * Initialize with some sample data (optional)
 */
export const initializeSampleMovements = (): void => {
  const sampleMovements = [
    {
      id: "MOV001-1",
      reference: "SM-2024-001",
      date: new Date("2024-01-15"),
      type: "Purchase",
      source: "Manual" as const,
      stockCode: "WGT-A",
      stockDescription: "Widget Type A",
      location: "A01-R01-S01-B01",
      quantity: 100,
      unitPrice: 15.00,
      lineTotal: 1500.00,
      userName: "John Smith",
      notes: "Initial stock purchase",
      createdAt: new Date("2024-01-15").toISOString(),
    },
    {
      id: "MOV002-1",
      reference: "SM-2024-002",
      date: new Date("2024-01-16"),
      type: "Sales",
      source: "Manual" as const,
      stockCode: "WGT-A",
      stockDescription: "Widget Type A",
      location: "A01-R01-S01-B01",
      quantity: -25,
      unitPrice: 25.00,
      lineTotal: 625.00,
      userName: "Sarah Johnson",
      notes: "Customer order #1234",
      customerReference: "CUST-1234",
      createdAt: new Date("2024-01-16").toISOString(),
    },
  ];

  stockMovements.push(...sampleMovements);
  nextId = 3;
};
